import { useQuery } from "@tanstack/react-query";
import { problemOf, scholarshipsApi } from "../../../api/scholarships";
import { SCHOLARSHIPS_KEY } from "../model";
import { openDocument } from "../review/model";

// BRD's side of government loans: tickets, payments and messages once the student accepts

export const BRD_KEY = [...SCHOLARSHIPS_KEY, "brd"];
export const brdLoanKey = (id) => [...BRD_KEY, id];

// Abroad, BRD pays the university and sends allowances through the embassy; in Rwanda it pays the
// institution and the student
export const paymentKindsFor = (travels) => [
  { value: "TUITION", label: travels ? "Tuition paid to the university" : "Tuition paid to the institution" },
  { value: "ALLOWANCE", label: travels ? "Allowance sent to the embassy" : "Allowance paid to the student" },
];
export const paymentKindOf = (value, travels) => paymentKindsFor(travels).find((k) => k.value === value)?.label ?? value;

/**
 * Where BRD's loans live and how to act on them. The pages read every loan in this shape (the
 * scholarships API's); another API's source maps its own (abroadLoans/awards.js). Calls that
 * change a loan resolve to `{ data }` with the loan as it is now; `openLetter` is null when BRD
 * cannot read the letter there.
 */
export const SCHOLARSHIP_LOANS = {
  key: BRD_KEY,
  pathOf: (id) => `/loan-payments/${id}`,
  list: async () => (await scholarshipsApi.get("staff/brd/loans")).data,
  one: async (id) => (await scholarshipsApi.get(`staff/brd/loans/${id}`)).data,
  openLetter: (id) => openDocument(`staff/applications/${id}/loan/letter/download-url`),
  openVisa: (id) => openDocument(`staff/applications/${id}/travel/visa/download-url`),
  openTicket: (id) => openDocument(`staff/applications/${id}/travel/ticket/download-url`),
  uploadTicket: (id, body) => scholarshipsApi.post(`staff/brd/loans/${id}/ticket`, body),
  recordPayment: (id, body) => scholarshipsApi.post(`staff/brd/loans/${id}/payments`, body),
  removePayment: (id, paymentId) => scholarshipsApi.delete(`staff/brd/loans/${id}/payments/${paymentId}`),
  messages: (id) => ({ path: `staff/applications/${id}/messages` }),
};

export const useBrdLoans = (source = SCHOLARSHIP_LOANS) =>
  useQuery({
    queryKey: source.key,
    queryFn: source.list,
  });

export const useBrdLoan = (id, source = SCHOLARSHIP_LOANS) =>
  useQuery({
    queryKey: [...source.key, id],
    enabled: Boolean(id),
    retry: (count, error) => problemOf(error)?.status !== 404 && count < 2,
    queryFn: () => source.one(id),
  });
